import { useLazyQuery, useQuery } from "@apollo/client/react"; import { useMemo, useState, useEffect } from 'react';
import { useDebounce } from '../../../hooks/useDebounce';

import { Box, Grid } from '@mui/material';
import { TextInput } from '../../../components/textInput/TextInput';
import DataGridGenerico from '../../../components/dataGrid/DataGridGenerico';
import { generateColumnsDefaultConfiguration } from '../../../components/dataGrid/columnHelpers';
import { CustomDialog } from "../../../components/customDialog/CustomDialog";
import { RenderInputsRowByType } from '../../../components/formGroupGenerator/RenderInputRowByType';
import { GET_PROFESIONALES_ASIGNACION_PAGINATED, adaptProfesionalesArray } from '../../../data/graphQL/queries/ResumenIngresoValidation/getProfesionalesAsignacion';
import { GET_ALL_TIPO_PROFESIONAL_ESPECIALIDAD } from '../../../data/graphQL/queries/PlanCuidado/getTipoProfesionalEspecialidad';
import { adaptAllTipoProfesionalEspecialidadArray } from '../../../adapters/allTipoProfesionalEspecialidadAdapter';
import AnnouncementIcon from '@mui/icons-material/AnnouncementTwoTone';

export const Profesionales = ({
  idDelegacion,
  idTipoModulo,
  handleRowClick,
  loading,
  variant,
  name,
  notification
}) => {
  const [openModal, setOpenModal] = useState(false);
  const [tipoProfesionalesEspecialidad, setTipoProfesionalesEspecialidad] = useState(false);

  const [
    rows,
    totalCount,
    filterStatus,
    setFilterStatus] =
    useGetProfesionales(idDelegacion, idTipoModulo, notification);

  const newInput = useMemo(() => {
    return {
      displayName: name || 'Asignar',
      name: 'asignarProfesional',
      type: 'button',
      variant: variant || 'contained',
      isLoading: loading,
      col: 30,
      onClick: () => handleOpenModal()
    };
  }, [loading]);

  const { data: tipoProfesionalesEspecialidadData } = useQuery(GET_ALL_TIPO_PROFESIONAL_ESPECIALIDAD, {
    fetchPolicy: 'no-cache',
  });

  useEffect(() => {
    if (!tipoProfesionalesEspecialidadData) return;

    setTipoProfesionalesEspecialidad(adaptAllTipoProfesionalEspecialidadArray(tipoProfesionalesEspecialidadData));
  }, [tipoProfesionalesEspecialidadData]);

  const handleOpenModal = () => setOpenModal(true);
  const handleClose = () => setOpenModal(false);

  const profesionalesColumns = [
    {
      field: 'codigoProfesional',
      headerName: 'Id',
      flex: 0.15
    },
    {
      field: 'tipoProfesional',
      headerName: 'Tipo',
      flex: 0.20
    },
    {
      field: 'idEspecialidad',
      headerName: 'Especialidad',
      flex: 0.25,
      valueGetter: (idEspecialidad) => {
        return tipoProfesionalesEspecialidad?.find((x) => x.id === idEspecialidad)?.displayName;
      }
    },
    {
      field: 'apellido',
      headerName: 'Apellido',
      flex: 0.20
    },
    {
      field: 'nombre',
      headerName: 'Nombre',
      flex: 0.20
    }];


  const columns = useMemo(() => generateColumnsDefaultConfiguration(profesionalesColumns), [profesionalesColumns, tipoProfesionalesEspecialidad]);

  return <>
    <RenderInputsRowByType
      input={newInput}
      value={null}
      handleChange={null} />

    <CustomDialog
      maxWidth='lg'
      fullWidth
      onCancel={handleClose}
      open={openModal}
      cancelText='Cerrar'
      title='Asignación de profesional'>

      <Grid container justifyContent={'space-between'} spacing={2} sx={{ width: '100%' }}>
        <Grid size={{ xs: 12, md: 6 }}>
          <p>Haz click sobre un profesional para seleccionarlo.</p>
          <Box sx={{ my: 2.5 }}>
            <TextInput
              placeholder='Filtrar'
              value={filterStatus}
              searchIcon
              onChange={(e) => setFilterStatus(e.target.value)}
              onClearButtonClick={() => setFilterStatus('')} />

          </Box>
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <Box sx={{
            display: 'flex',
            border: '1px solid #b7b7b7',
            borderRadius: '8px',
            padding: '12px',
            background: '#f5ff603d',
            alignItems: 'center'
          }}>
            <AnnouncementIcon sx={{ color: '#f1c63b', marginRight: '12px' }} />
            <p style={{ margin: 0, fontSize: 11, whiteSpace: 'pre-line' }}>
              {` Los profesionales que aparecen en esta lista son aquellos que:
                            1. Están activos en el sistema.
                            2. Están asignados a la delegación seleccionada.
                            3. Poseen la firma ológrafa digitalizada.
                            4. Poseen monitoreo activo.
                            5. Cuentan con seguro de mala praxis vigente.
                            6. Poseen matrícula profesional vigente.`}
            </p>
          </Box>
        </Grid>
        <Grid size={{ xs: 12 }}>
          <DataGridGenerico
            hideFooterPagination={false}
            toolbar={false}
            columns={columns}
            rows={rows}
            totalCount={totalCount}
            onRowClick={(row) => { handleRowClick(row.row); handleClose(); }} />

        </Grid>
      </Grid>
    </CustomDialog>
  </>;
};

const useGetProfesionales = (idDelegacion, idTipoModulo, notification) => {
  const [datosOriginal, setDatosOriginal] = useState([]);

  const [filterStatus, setFilterStatus] = useState('');
  const debouncedFilterValue = useDebounce(filterStatus, 1000);

  const [getProfesionales, { loading: profesionalesLoading }] = useLazyQuery(GET_PROFESIONALES_ASIGNACION_PAGINATED, {
    fetchPolicy: 'no-cache'
  });

  useEffect(() => {
    if (!profesionalesLoading) {
      getProfesionales({
        variables: {
          idDelegacion: idDelegacion,
          idTipoModulo: idTipoModulo
        }
      }).then((res) => {
        setDatosOriginal(adaptProfesionalesArray(res?.data?.profesionalsForRIPaginated || []));
      });
    }
  }, [idDelegacion, idTipoModulo]);

  const profesionalesFiltered = useMemo(() => {
    if (!debouncedFilterValue) return datosOriginal;

    const filter = debouncedFilterValue.toLowerCase().trim();
    return datosOriginal.filter((profesional) => {
      return (
        profesional.nombre?.toLowerCase()?.includes(filter) ||
        profesional.apellido?.toLowerCase()?.includes(filter) ||
        profesional.tipoProfesional?.toLowerCase()?.includes(filter) ||
        profesional.codigoProfesional?.toString()?.includes(filter) ||
        profesional.especialidad?.toString()?.includes(filter));

    });
  }, [debouncedFilterValue, datosOriginal]);

  return [
    profesionalesFiltered,
    datosOriginal.length || 0,
    filterStatus,
    setFilterStatus];

};